import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../modules/layout/components/ToastContext";
import { deleteLibraryTransactionRoute, getAll, getById, getLibraryContents, getLibrarySyncOverview, syncLibraryAcrossSites, syncLibraryFromFileSystem, syncLibraryDatabases } from "../_request";
import { ILibraryDto, IFilterLibraryModel, IServiceDto, IAxiosGetResponse, ILibraryContentDto, ILibrarySyncOverviewDto } from "../../interfaces/IUtilities";
import { Link, useNavigate, useParams } from "react-router-dom";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import { FileEarmarkText, Trash3 } from "react-bootstrap-icons";
import { Field, Form, Formik } from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import SortDirection from "../../modules/layout/components/SortDirection";
import ConfirmModal from "../../modules/layout/components/ConfirmModal";
import { Plus, CloudUpload } from "react-bootstrap-icons";
import Pagination from "../../modules/layout/components/Pagination";
import { Pencil } from "react-bootstrap-icons";
import LibraryTreeModal from "./LibraryTreeModal";

const LibraryList = () => {
    const { t } = useTranslation();
    const { serviceid, transactionid } = useParams<{
        serviceid: string;
        transactionid: string;
    }>();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [service, setService] = useState({} as string);
    const [transaction, setTransaction] = useState({} as string);
    const [libraries, setLibraries] = useState<ILibraryDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortField, setSortField] = useState("name");
    const [asc, setAsc] = useState(true);
    const order = asc ? "asc" : "desc";

    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [selectedLibraryPath, setSelectedLibraryPath] = useState<string | null>(null);
    const [libraryContent, setLibraryContent] = useState<ILibraryContentDto | null>(null);
    const [libraryContentLoading, setLibraryContentLoading] = useState(false);
    const [syncOverview, setSyncOverview] = useState<ILibrarySyncOverviewDto | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [includeBcc, setIncludeBcc] = useState(true);

    const [pageSize, setPageSize] = useState<number>(10);

    const initialFilters: IFilterLibraryModel = {
        name: "",
        libraryPath: "",
    };

    const fetchServiceName = async () => {
        try {
            let result = await getById("/api/services/", serviceid! + "/name");
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as string);
            } else {
                showToast(t("CouldNotFetchServiceName", { id: serviceid }), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    }

    const fetchClassName = async () => {
        try {
            let result = await getById("/api/transactionroutes/", transactionid + "/classname");
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransaction(result.data as string);
            } else {
                showToast(t("CouldNotFetchClassName", { id: transactionid }), "error");
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            showToast(t("ConnectionError", { error: String(error) }), "error");
        }
    };

    const [activeFilters, setActiveFilters] = useState<IFilterLibraryModel>(initialFilters);

    const fetchLibraries = async (filters: IFilterLibraryModel = {}) => {
        setLoading(true);
        try {
            const params = [
                { key: "page", value: page },
                { key: "pageSize", value: pageSize },
                { key: "sortBy", value: sortField },
                { key: "order", value: order },
            ];

            if (filters.name) params.push({ key: "name", value: filters.name });
            if (filters.libraryPath) params.push({ key: "libraryPath", value: filters.libraryPath });

            const queryString =
                "?" +
                params
                    .map(
                        (f) => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`
                    )
                    .join("&");

            const result = await getAll(
                `/api/library/bytransactionroute/${transactionid}`,
                queryString
            );

            let respStatus = result?.status || result?.response.status;
            if (respStatus === 403) 
                navigate("/error/403");
            
            const items = result?.data?.items ?? [];
            setLibraries(items);
            const totalCount = result?.data?.totalCount ?? 0;
            const pages = Math.ceil(totalCount / pageSize);
            setTotalPages(pages > 0 ? pages : 1);
            setTotal(result?.data.totalCount);
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServiceName();
        fetchLibraries(activeFilters);
        fetchClassName();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const handleFilterSearch = async (filters: IFilterLibraryModel) => {
        setActiveFilters(filters);
        setPage(1);
        await fetchLibraries(filters);
    };

    const handleDeleteClick = (id: number, name: string) => {
        setSelectedId(id);
        setSelectedName(name);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId && transactionid) {
            setShowModal(false);

            try {
                const result = await deleteLibraryTransactionRoute(selectedId, transactionid);

                if (result.status === 200) {
                    showToast(t('LibraryAssociationDeleted'), 'success');
                }
                else if (result.status === 400) {
                    showToast(t('LibraryPathNotExists'), 'warning');
                } 
                else if (result.status === 404) {
                    showToast(t('LibraryDirectoryNotExists'), 'warning');
                } 
                else {
                    showToast(t('DeleteWarningWithStatus', { status: result.status, data: result.data }), 'warning');
                }
                await fetchLibraries(activeFilters);
            } catch (err) {
                showToast(t('ErrorWithError', { error: String(err) }), 'error');
            } finally {
                setSelectedId(null);
            }
        }
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedId(null);
    };

    const handleDetailsClick = async (libraryPath: string) => {
        setSelectedLibraryPath(libraryPath);
        setShowDetailsModal(true);
        setLibraryContentLoading(true);
        setLibraryContent(null);

        try {
            const result = await getLibraryContents(libraryPath);
            const respStatus = result?.status || result?.response?.status;

            if (respStatus === 200) {
                setLibraryContent(result.data as ILibraryContentDto);
            } else {
                showToast(t("CouldNotFetchLibraryContents"), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setLibraryContentLoading(false);
        }
    };

    const handleSyncStatusClick = async (libraryPath: string) => {
        setSelectedLibraryPath(libraryPath);
        setShowSyncModal(true);
        setSyncLoading(true);
        setSyncOverview(null);
        try {
            const result = await getLibrarySyncOverview(libraryPath, includeBcc);
            const respStatus = result?.status || result?.response?.status || 200;
            if (respStatus === 200) {
                setSyncOverview(result.data as ILibrarySyncOverviewDto);
            } else {
                showToast(t("CouldNotFetchSyncStatus"), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncClose = () => {
        setShowSyncModal(false);
        setSelectedLibraryPath(null);
        setSyncOverview(null);
    };

    const handleSyncFromFileSystem = async () => {
        if (!selectedLibraryPath) return;
        setSyncLoading(true);
        try {
            const result = await syncLibraryFromFileSystem(selectedLibraryPath, includeBcc);
            const respStatus = result?.status || result?.response?.status || 200;
            if (respStatus === 200 || respStatus === 204) {
                showToast(t("SyncFromFileSystemTriggered"), "success");
                handleSyncClose();
            } else {
                showToast(t("SyncFromFileSystemFailed"), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncDatabases = async () => {
        if (!selectedLibraryPath) return;
        setSyncLoading(true);
        try {
            const result = await syncLibraryDatabases(selectedLibraryPath);
            const respStatus = result?.status || result?.response?.status || 200;
            if (respStatus === 200 || respStatus === 204) {
                showToast(t("SyncDatabasesTriggered"), "success");
                handleSyncClose();
            } else {
                showToast(t("SyncDatabasesFailed"), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setSyncLoading(false);
        }
    };

    const triggerSyncForSite = async (target: "primary" | "bcc") => {
        if (!selectedLibraryPath) return;
        setSyncLoading(true);
        try {
            const result = await syncLibraryAcrossSites(
                selectedLibraryPath,
                target === "primary",
                target === "bcc"
            );
            const respStatus = result?.status || result?.response?.status || 200;
            if (respStatus === 200 || respStatus === 204) {
                showToast(t("SyncTriggered"), "success");
                handleSyncClose();
            } else {
                showToast(t("SyncFailed"), "error");
            }
        } catch (error) {
            showToast(t("ConnectionError", { error: String(error) }), "error");
        } finally {
            setSyncLoading(false);
        }
    };

    const hasDbDiscrepancy = syncOverview ? !syncOverview.dbStatus?.isInSync : false;
    const primaryFsStatus = syncOverview?.primaryFileSystem?.status;
    const hasPrimaryFsDiscrepancy = syncOverview ? !(primaryFsStatus?.allFilesPresent ?? false) : false;
    const bccFsStatus = syncOverview?.bccFileSystem?.status;
    const hasBccFsDiscrepancy = syncOverview?.bccFileSystem ? !(bccFsStatus?.allFilesPresent ?? false) : false;
    const canSyncFromFs = syncOverview
        ? (syncOverview.dbStatus?.localFileCount ?? 0) === 0 &&
        (syncOverview.primaryFileSystem?.status?.existingFiles?.length ?? 0) > 0
        : false;

    const handleCloseDetails = () => {
        setShowDetailsModal(false);
        setSelectedLibraryPath(null);
        setLibraryContent(null);
    };

    return (
        <div className="container mt-4">
            <AppBreadcrumb
                items={[
                    { title: t("Home"), href: "/" },
                    { title: t("Services"), href: "/services/list" },
                    {
                        title: `${service}: ${t("TransactionRoutes")}`,
                        href: `/services/${serviceid}/transactions/list`,
                    },
                    {
                        title: `${transaction}: ${t("Libraries")}`,
                        href: `/services/${serviceid}/transactions/${transactionid}/libraries/list`,
                    },
                ]}
            />

            <h3>{t("LibrariesLinkedToTransactionRoute")}</h3>

            <div className="d-flex gap-3 mb-3">
                <button
                    className="btn btn-success gg-btn-submit d-flex align-items-center gap-2"
                    onClick={() =>
                        navigate(`/services/${serviceid}/transactions/${transactionid}/libraries/add`)
                    }
                >
                    <Plus />
                    {t("AddExistingLibrary")}
                </button>

                <button
                    className="btn gg-btn d-flex align-items-center gap-2"
                    onClick={() =>
                        navigate(`/services/${serviceid}/transactions/${transactionid}/libraries/upload`)
                    }
                >
                    <CloudUpload />
                    {t("Upload")}
                </button>
            </div>

            <FiltersCard>
                <Formik initialValues={initialFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>
                            <div className="row g-3">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label">{t("Name")}</label>
                                    <Field className="form-control" name="name" />
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label">{t("LibraryPath")}</label>
                                    <Field className="form-control" name="libraryPath" />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-auto">
                                    <button type="submit" className="btn gg-btn gg-btn-submit me-2">
                                        {t("Search")}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            resetForm();
                                            setActiveFilters(initialFilters);
                                            setPage(1);
                                            fetchLibraries(initialFilters);
                                        }}
                                    >
                                        {t("Reset")}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort("name")}>
                                        {t("Name")}{" "}
                                        <SortDirection isActive={sortField === "name"} order={order} />
                                    </th>
                                    <th onClick={() => handleSort("libraryPath")}>
                                        {t("LibraryPath")}{" "}
                                        <SortDirection
                                            isActive={sortField === "libraryPath"}
                                            order={order}
                                        />
                                    </th>
                                    <th className="text-wrap">{t("Actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {libraries.length > 0 ? (
                                    libraries.map((lib) => (
                                        <tr key={lib.id}>
                                            <td>{lib.name}</td>
                                            <td>{lib.libraryPath}</td>
                                            <td className=" text-wrap d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-info"
                                                    onClick={() => handleDetailsClick(lib.libraryPath)}
                                                    title={t('Details')}
                                                >
                                                    <FileEarmarkText size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-secondary"
                                                    onClick={() => handleSyncStatusClick(lib.libraryPath)}
                                                    title={t('SyncStatus')}
                                                >
                                                    {t("Sync")}
                                                </button>
                                                <Link
                                                    title={t('Edit')}
                                                    to={`/services/${serviceid}/transactions/${transactionid}/libraries/edit/${lib.id}`}
                                                    className="btn gg-btn btn-sm me-2"
                                                >
                                                    <Pencil size={16} className="text-danger" />
                                                </Link>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteClick(lib.id, lib.name)}
                                                >
                                                    <Trash3 size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={3}>{t("NoLibrariesFound")}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    <ConfirmModal
                        show={showModal}
                        title={t("Delete")}
                        message={t("AreYouSureRemoveLibrary", {
                            name: selectedName ?? "",
                        })}
                        onConfirm={handleConfirmDelete}
                        onCancel={handleCancelDelete}
                    />

                    <LibraryTreeModal
                        show={showDetailsModal}
                        onClose={handleCloseDetails}
                        libraryPath={selectedLibraryPath}
                        content={libraryContent}
                        loading={libraryContentLoading}
                    />

                    {showSyncModal && (
                        <div className="modal d-block" tabIndex={-1} role="dialog">
                            <div className="modal-dialog modal-xl">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">
                                            {t("SyncStatusFor", { libraryPath: selectedLibraryPath })}
                                        </h5>
                                        <button type="button" className="btn-close" aria-label="Close" onClick={handleSyncClose}></button>
                                    </div>
                                    <div className="modal-body">
                                        {syncLoading && <Loader />}
                                        {!syncLoading && syncOverview && (
                                            <>
                                                <div className="mb-3">
                                                    <strong>{t("DatabaseSyncStatus")}</strong>
                                                    <p className={syncOverview.dbStatus.isInSync ? "text-success" : "text-danger"}>
                                                        {syncOverview.dbStatus.isInSync ? t("AllFilesInSync") : t("DiscrepanciesFound")}
                                                    </p>
                                                    {!syncOverview.dbStatus.isInSync && (
                                                        <button
                                                            className="btn btn-sm btn-warning mb-2"
                                                            onClick={handleSyncDatabases}
                                                            disabled={syncLoading}
                                                        >
                                                            {t("SyncDatabasesOnly")}
                                                        </button>
                                                    )}
                                                    {!syncOverview.dbStatus.isInSync && (
                                                        <ul className="list-unstyled small">
                                                            {syncOverview.dbStatus.missingInTransformation.length > 0 && (
                                                                <li>
                                                                    <strong>{t("MissingInTransformation")}:</strong>{" "}
                                                                    {syncOverview.dbStatus.missingInTransformation.map(f => f.fileName).join(", ")}
                                                                </li>
                                                            )}
                                                            {syncOverview.dbStatus.extraInTransformation.length > 0 && (
                                                                <li>
                                                                    <strong>{t("ExtraInTransformation")}:</strong>{" "}
                                                                    {syncOverview.dbStatus.extraInTransformation.map(f => f.fileName).join(", ")}
                                                                </li>
                                                            )}
                                                            {syncOverview.dbStatus.hashMismatches.length > 0 && (
                                                                <li>
                                                                    <strong>{t("HashMismatches")}:</strong>{" "}
                                                                    {syncOverview.dbStatus.hashMismatches.map(f => f.fileName).join(", ")}
                                                                </li>
                                                            )}
                                                        </ul>
                                                    )}
                                                </div>
                                                {canSyncFromFs && (
                                                    <div className="mb-3">
                                                        <button
                                                            className="btn btn-sm btn-warning"
                                                            onClick={handleSyncFromFileSystem}
                                                            disabled={syncLoading}
                                                        >
                                                            {t("SyncFromFileSystem")}
                                                        </button>
                                                        <div className="small text-muted mt-1">
                                                            {t("SyncFromFileSystemHint")}
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="row g-4">
                                                    <div className="col-lg-6">
                                                        <h6>{t("PrimarySite")}</h6>
                                                        <p className={
                                                            syncOverview.primaryFileSystem.errorMessage
                                                                ? "text-danger"
                                                                : (syncOverview.primaryFileSystem.status?.allFilesPresent ? "text-success" : "text-danger")
                                                        }>
                                                            {syncOverview.primaryFileSystem.errorMessage
                                                                ? `Filesystem check failed: ${syncOverview.primaryFileSystem.errorMessage}`
                                                                : (syncOverview.primaryFileSystem.status?.allFilesPresent ? t("AllFilesPresent") : t("MissingFiles"))}
                                                        </p>
                                                        {!syncOverview.primaryFileSystem.errorMessage && !syncOverview.primaryFileSystem.status?.allFilesPresent && (
                                                            <div className="small">{syncOverview.primaryFileSystem.status?.missingFiles.join(", ")}</div>
                                                        )}
                                                        <button
                                                            className="btn btn-sm btn-primary mt-2"
                                                            onClick={() => triggerSyncForSite("primary")}
                                                            disabled={syncLoading || ((syncOverview.primaryFileSystem.status?.allFilesPresent ?? false) && syncOverview.dbStatus.isInSync)}
                                                        >
                                                            {t("SyncPrimary")}
                                                        </button>
                                                    </div>
                                                    {includeBcc && syncOverview.bccFileSystem && (
                                                        <div className="col-lg-6">
                                                            <h6>{t("BccSite")}</h6>
                                                            <p className={
                                                                syncOverview.bccFileSystem.errorMessage
                                                                    ? "text-danger"
                                                                    : (syncOverview.bccFileSystem.status?.allFilesPresent ? "text-success" : "text-danger")
                                                            }>
                                                                {syncOverview.bccFileSystem.errorMessage
                                                                    ? `Filesystem check failed: ${syncOverview.bccFileSystem.errorMessage}`
                                                                    : (syncOverview.bccFileSystem.status?.allFilesPresent ? t("AllFilesPresent") : t("MissingFiles"))}
                                                            </p>
                                                            {!syncOverview.bccFileSystem.errorMessage && !syncOverview.bccFileSystem.status?.allFilesPresent && (
                                                                <div className="small">{syncOverview.bccFileSystem.status?.missingFiles.join(", ")}</div>
                                                            )}
                                                            <button
                                                                className="btn btn-sm btn-primary mt-2"
                                                                onClick={() => triggerSyncForSite("bcc")}
                                                                disabled={syncLoading || ((syncOverview.bccFileSystem.status?.allFilesPresent ?? false) && syncOverview.dbStatus.isInSync)}
                                                            >
                                                                {t("SyncBcc")}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={handleSyncClose} disabled={syncLoading}>
                                            {t("Close")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <Pagination currentPage={page}
                        pageSize={pageSize}
                        onPageChange={(page) => { setPage(page) }}
                        onPageSizeChange={(size) => { setPageSize(size) }}
                        totalItems={total} />

                </>
            )}
        </div>
    );
};

export default LibraryList;
